/**
 * Toast notification system — replaces native window.alert for friendlier UX.
 *
 * Usage:
 *   showToast({ type: "warning", message: "Please enter your email." });
 *   showToast({ type: "success", message: "Feedback sent. Thank you!" });
 *   showToast({ type: "error", message: "Sign-in failed. Please try again.", durationMs: 7000 });
 *
 * Properties:
 *   - Stacks in bottom-right (desktop) / top-center (mobile)
 *   - Auto-dismisses after durationMs (default 4500ms)
 *   - Hover pauses the dismiss timer
 *   - Up to 5 concurrent toasts; oldest gets evicted
 *   - Accessible: aria-live="polite"
 */

import { escapeHtml } from "./formatUtils";
import { feedbackResult, feedbackResultIcon, type FeedbackResultKind } from './feedbackResult';

export type ToastType = Exclude<FeedbackResultKind, 'loading'>;

export interface ToastOptions {
  id?: string;
  type?: ToastType;
  message: string;
  durationMs?: number;
  title?: string;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Media retries in some Android browsers must start in pointerdown rather
   * than the later click task, otherwise the browser no longer considers the
   * gesture eligible for HTMLMediaElement.play().
   */
  actionEvent?: 'click' | 'pointerdown';
}

const TOAST_MAX = 5;
const DEFAULT_DURATION = 4500;
const PAUSE_HOVER = true;

let toastSeq = 0;

function ensureContainer(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  let el = document.getElementById("toastContainer");
  if (!el) {
    el = document.createElement("div");
    el.id = "toastContainer";
    el.className = "toast-container";
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "false");
    document.body.appendChild(el);
  }
  return el;
}

export function showToast(options: ToastOptions): void {
  const type: ToastType = options.type ?? "info";
  const message = options.message ?? "";
  const duration = options.durationMs ?? DEFAULT_DURATION;
  if (!message) return;
  const result = feedbackResult(type, {
    detail: message,
    ...(options.title === undefined ? {} : { title: options.title }),
  });
  const container = ensureContainer();
  if (!container) return;

  if (options.id) document.getElementById(options.id)?.remove();

  // Evict oldest if at cap.
  const existing = container.querySelectorAll(".toast");
  if (existing.length >= TOAST_MAX) {
    const first = existing[0];
    dismissToast(first as HTMLElement, true);
  }

  const id = options.id ?? `toast-${++toastSeq}`;
  const el = document.createElement("div");
  el.className = `toast toast-${type}`;
  el.id = id;
  el.dataset.feedbackKind = result.kind;
  el.setAttribute("role", type === "error" ? "alert" : "status");
  const actionMarkup = options.actionLabel && options.onAction
    ? `<button class="toast-action" type="button">${escapeHtml(options.actionLabel)}</button>`
    : '';
  el.innerHTML = `
    <div class="toast-icon" style="color:var(${result.token})">${feedbackResultIcon(result.kind)}</div>
    <div class="toast-body">
      <div class="toast-title">${escapeHtml(result.title)}</div>
      <div class="toast-msg">${escapeHtml(result.detail)}</div>
      ${actionMarkup}
    </div>
    <button class="toast-close" type="button" aria-label="Dismiss notification">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
    </button>
  `;

  container.appendChild(el);

  // Trigger enter animation on next frame.
  requestAnimationFrame(() => {
    el.classList.add("toast-show");
  });

  const closeBtn = el.querySelector(".toast-close") as HTMLButtonElement | null;
  const actionBtn = el.querySelector('.toast-action') as HTMLButtonElement | null;
  let timer: number | null = null;

  const startTimer = () => {
    if (duration <= 0) return;
    timer = window.setTimeout(() => dismissToast(el, true), duration);
  };
  const clearTimer = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  if (PAUSE_HOVER) {
    el.addEventListener("mouseenter", clearTimer);
    el.addEventListener("mouseleave", startTimer);
  }

  if (closeBtn) {
    closeBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      dismissToast(el, true);
    });
  }
  if (actionBtn && options.onAction) {
    let actionHandled = false;
    const handleAction = () => {
      if (actionHandled) return;
      actionHandled = true;
      clearTimer();
      dismissToast(el, true);
      options.onAction?.();
    };
    if (options.actionEvent === 'pointerdown') {
      actionBtn.addEventListener('pointerdown', (event) => {
        // The retry itself must stay in this trusted input task. Preventing
        // the synthetic follow-up click also avoids a duplicate retry.
        event.preventDefault();
        handleAction();
      }, { once: true });
      actionBtn.addEventListener('click', handleAction, { once: true });
    } else actionBtn.addEventListener('click', handleAction, { once: true });
  }

  startTimer();
}

function dismissToast(el: HTMLElement, animate: boolean): void {
  if (!el || !el.isConnected) return;
  if (animate) {
    el.classList.add("toast-leave");
    el.classList.remove("toast-show");
    window.setTimeout(() => el.remove(), 220);
  } else {
    el.remove();
  }
}

export function dismissToastById(id: string): void {
  const toast = typeof document === 'undefined' ? null : document.getElementById(id);
  if (toast instanceof HTMLElement) dismissToast(toast, true);
}

export function clearAllToasts(): void {
  if (typeof document === "undefined") return;
  document.querySelectorAll(".toast").forEach((node) => dismissToast(node as HTMLElement, true));
}
